import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft } from 'lucide-react';
import useWebSocket from '../hooks/useWebSocket';
import MessageList from '../components/MessageList';
import MessageInput from '../components/MessageInput';

function ChatHeader({ matchInfo, otherTyping, wsConnected, onBack }) {
  return (
    <header className="px-6 py-4 flex items-center gap-4" style={{borderBottom:'1px solid var(--border-color)'}}>
      <button onClick={onBack} className="p-2 rounded-xl transition-colors hover:bg-[var(--bg-card-hover)]" data-testid="chat-back-btn">
        <ArrowLeft size={20} style={{color:'var(--text-secondary)'}} />
      </button>
      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0" style={{background:'var(--bg-card)'}}>
        {matchInfo?.photos?.[0] ? (
          <img src={matchInfo.photos[0]} alt={matchInfo.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-bold" style={{color:'var(--text-muted)'}}>{matchInfo?.name?.[0]}</div>
        )}
      </div>
      <div className="flex-1">
        <p className="font-bold text-sm" style={{color:'var(--text-primary)'}}>{matchInfo?.name || 'Chargement...'}</p>
        <p className="text-xs" style={{color: otherTyping ? '#a855f7' : 'var(--text-muted)'}}>
          {otherTyping ? "En train d'ecrire..." : wsConnected ? 'En ligne' : 'Hors ligne'}
        </p>
      </div>
      {wsConnected && <div className="w-2.5 h-2.5 rounded-full bg-green-500" title="Connecte en temps reel" />}
    </header>
  );
}

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [matchInfo, setMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/chat/messages/${matchId}`);
      setMessages(res.data || []);
    } catch (e) { /* ignore */ }
  }, [matchId]);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'new_message') {
      setMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, {
          id: data.id, sender_id: data.sender_id,
          content: data.text, text: data.text,
          created_at: data.timestamp, read: data.read,
          reactions: data.reactions || [], deleted: data.deleted || false,
        }];
      });
    } else if (data.type === 'read') {
      setMessages(prev => prev.map(m => m.sender_id === user?.id ? { ...m, read: true } : m));
    }
  }, [user?.id]);

  const { connected, otherTyping, sendMessage, sendTyping } = useWebSocket(matchId, user?.id, handleWsMessage);

  useEffect(() => {
    let pollInterval;
    const init = async () => {
      try {
        const matchRes = await api.get('/matches');
        const m = matchRes.data?.find(x => x.id === matchId);
        if (m) setMatchInfo(m.other_user);
        await loadMessages();
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    init();
    if (!connected) pollInterval = setInterval(loadMessages, 4000);
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [matchId, connected, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async (text) => {
    const sentViaWs = sendMessage(text);
    if (!sentViaWs) {
      try {
        await api.post('/chat/send', { match_id: matchId, content: text, text, type: 'text' });
        await loadMessages();
      } catch (e) { /* ignore */ }
    }
  }, [matchId, sendMessage, loadMessages]);

  const handleReact = useCallback(async (messageId, emoji) => {
    try {
      await api.post('/chat/react', { message_id: messageId, emoji });
      await loadMessages();
    } catch (e) { /* ignore */ }
  }, [loadMessages]);

  return (
    <div className="h-full flex flex-col" data-testid="chat-page">
      <ChatHeader matchInfo={matchInfo} otherTyping={otherTyping} wsConnected={connected} onBack={() => navigate('/matches')} />

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <MessageList messages={messages} userId={user?.id} onReact={handleReact} />
        )}
        <div ref={bottomRef} />
      </div>

      <MessageInput onSend={handleSend} onTyping={sendTyping} />
    </div>
  );
}
